import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useTranslation } from 'react-i18next';

export interface ExportOptions {
  includePhotos: boolean;
  includeHistory: boolean;
  includeMessages: boolean;
}

interface TicketExportOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (options: ExportOptions) => void;
}

export const TicketExportOptionsModal: React.FC<TicketExportOptionsModalProps> = ({
  isOpen,
  onClose,
  onExport
}) => {
  const { t } = useTranslation(['pages.projectDetails']);
  const [options, setOptions] = useState<ExportOptions>({
    includePhotos: true,
    includeHistory: true,
    includeMessages: true
  });

  const handleOptionChange = (key: keyof ExportOptions, checked: boolean) => {
    setOptions(prev => ({
      ...prev,
      [key]: checked
    }));
  };

  const handleExport = () => {
    onExport(options);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('settings.pdfExport.optionsModal.title')}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="includePhotos"
              checked={options.includePhotos}
              onCheckedChange={(checked) => handleOptionChange('includePhotos', checked === true)}
            />
            <label
              htmlFor="includePhotos"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              {t('settings.pdfExport.optionsModal.includePhotos')}
            </label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="includeHistory"
              checked={options.includeHistory}
              onCheckedChange={(checked) => handleOptionChange('includeHistory', checked === true)}
            />
            <label
              htmlFor="includeHistory"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              {t('settings.pdfExport.optionsModal.includeHistory')}
            </label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="includeMessages"
              checked={options.includeMessages}
              onCheckedChange={(checked) => handleOptionChange('includeMessages', checked === true)}
            />
            <label
              htmlFor="includeMessages"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              {t('settings.pdfExport.optionsModal.includeMessages')}
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
          >
            {t('settings.pdfExport.optionsModal.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleExport}
          >
            {t('settings.pdfExport.optionsModal.export')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

